import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  Fragment,
} from 'react'
import {
  Box,
  VStack,
  HStack,
  Flex,
  Text,
  Button,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Select,
  Badge,
  Spinner,
  Avatar,
  Tooltip,
  Image,
  useToast,
  useColorModeValue,
  useBreakpointValue,
} from '@chakra-ui/react'
import {
  MagnifyingGlass,
  X,
  UserSwitch,
  ArrowUUpLeft,
  ArrowsClockwise,
  ArrowLeft,
  ArrowSquareOut,
  Robot,
  ChatsCircle,
  Clock,
  FileText,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import socketService, { SOCKET_EVENTS } from '../services/socket'
import { renderMessageWithLinks } from '../utils/messageUtils'
import { AudioPlayer } from '../components/AudioPlayer'
import { VideoThumbnail } from '../components/VideoPlayer/VideoThumbnail'

export interface AiChat {
  id: number
  protocol: string | null
  userName: string
  agentId: number
  agentName: string
  categoryName: string | null
  categoryColor: string | null
  state: 'ai_active' | 'human_queue' | 'human' | 'completed'
  unreadCountAgent: number
  lastMessage: {
    senderType: string
    preview: string
    createdAt?: string
  } | null
  createdAt: string
  updatedAt: string
}

export interface AiChatSummary {
  aiActive: number
  aiStale: number
  staleHours: number
  humanQueue: number
  human: number
  completed: number
  total: number
}

interface InboxAgent {
  id: number
  name: string
  categoryName: string | null
}

const CHAT_STATE_LABELS: Record<
  AiChat['state'],
  { label: string; colorScheme: string }
> = {
  ai_active: { label: 'Com a IA', colorScheme: 'green' },
  human_queue: { label: 'Na fila humana', colorScheme: 'orange' },
  human: { label: 'Com atendente', colorScheme: 'blue' },
  completed: { label: 'Concluído', colorScheme: 'gray' },
}

const PAGE_SIZE = 30

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

/** Hora no padrão do WhatsApp: hoje → HH:mm; ontem → "Ontem"; senão dd/mm/aa. */
function formatListTime(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  const now = new Date()
  if (isSameDay(date, now)) {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (isSameDay(date, yesterday)) return 'Ontem'
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

function formatDaySeparator(date: Date): string {
  const now = new Date()
  if (isSameDay(date, now)) return 'Hoje'
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (isSameDay(date, yesterday)) return 'Ontem'
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

function formatIdle(value: string): string {
  const minutes = Math.max(0, (Date.now() - new Date(value).getTime()) / 60000)
  if (minutes < 60) return `${Math.round(minutes)} min`
  const hours = minutes / 60
  if (hours < 24) return `${Math.round(hours)} h`
  return `${Math.round(hours / 24)} d`
}

export default function AiChatsInbox({
  agents,
  onSummaryChange,
}: {
  agents: InboxAgent[]
  onSummaryChange?: (summary: AiChatSummary) => void
}) {
  const [chats, setChats] = useState<AiChat[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [limit, setLimit] = useState(PAGE_SIZE)
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState<AiChatSummary>({
    aiActive: 0,
    aiStale: 0,
    staleHours: 6,
    humanQueue: 0,
    human: 0,
    completed: 0,
    total: 0,
  })
  const [agentFilter, setAgentFilter] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [actioningChatId, setActioningChatId] = useState<number | null>(null)

  const [selectedChat, setSelectedChat] = useState<AiChat | null>(null)
  const [messages, setMessages] = useState<any[]>([])
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [typingName, setTypingName] = useState<string | null>(null)

  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const navigate = useNavigate()
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, lg: false })

  const panelBg = useColorModeValue('white', 'gray.800')
  const headerBg = useColorModeValue('gray.50', 'gray.900')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50')
  const selectedBg = useColorModeValue('gray.100', 'whiteAlpha.100')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const wallpaperBg = useColorModeValue('#efeae2', '#0b141a')
  const bubbleOutBg = useColorModeValue('#d9fdd3', '#005c4b')
  const bubbleInBg = useColorModeValue('white', '#202c33')
  const bubbleText = useColorModeValue('gray.800', 'gray.100')
  const bubbleMeta = useColorModeValue('gray.500', 'whiteAlpha.600')
  const systemPillBg = useColorModeValue('#fff5c4', '#182229')
  const chipBg = useColorModeValue('gray.100', 'whiteAlpha.100')

  // Busca com debounce (filtra no servidor por nome, protocolo ou id)
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Filtro novo volta para o primeiro bloco da lista
  useEffect(() => {
    setLimit(PAGE_SIZE)
  }, [agentFilter, stateFilter, search])

  const fetchChats = useCallback(
    async (showSpinner = false) => {
      if (showSpinner) setIsLoading(true)
      try {
        const res = await api.get('/admin/ai-agents/chats', {
          params: {
            page: 1,
            limit,
            state: stateFilter || undefined,
            agentId: agentFilter || undefined,
            q: search || undefined,
          },
        })
        const data: AiChat[] = res.data.data || []
        setChats(data)
        if (res.data.summary) {
          setSummary(res.data.summary)
          onSummaryChange?.(res.data.summary)
        }
        setTotal(res.data.meta?.total ?? data.length)
        // mantém o cabeçalho da conversa aberta em dia (situação, agente)
        setSelectedChat((prev) =>
          prev ? data.find((c) => c.id === prev.id) || prev : prev,
        )
      } catch (error) {
        console.error('Error fetching AI chats:', error)
      } finally {
        if (showSpinner) setIsLoading(false)
      }
    },
    [limit, stateFilter, agentFilter, search, onSummaryChange],
  )

  useEffect(() => {
    fetchChats(true)
  }, [fetchChats])

  // Tempo real: a lista reage aos eventos de socket (sem polling)
  useEffect(() => {
    const scheduleRefetch = () => {
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current)
      refetchTimerRef.current = setTimeout(() => fetchChats(false), 600)
    }
    const events = [
      SOCKET_EVENTS.CHAT.NEW,
      SOCKET_EVENTS.CHAT.UPDATED,
      SOCKET_EVENTS.CHAT.STARTED,
      SOCKET_EVENTS.CHAT.FINISHED,
      SOCKET_EVENTS.CHAT.TRANSFERRED,
      SOCKET_EVENTS.CHAT.UNREAD_UPDATE,
    ]
    events.forEach((event) => socketService.on(event, scheduleRefetch))
    socketService.on('connect', scheduleRefetch)
    return () => {
      events.forEach((event) => socketService.off(event, scheduleRefetch))
      socketService.off('connect', scheduleRefetch)
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current)
    }
  }, [fetchChats])

  // ============ Conversa aberta (tempo real) ============

  const selectedChatId = selectedChat?.id ?? null

  const openChat = (chat: AiChat) => {
    if (chat.id === selectedChatId) return
    setSelectedChat(chat)
  }

  useEffect(() => {
    if (!selectedChatId) return
    let cancelled = false
    setMessages([])
    setIsLoadingMessages(true)
    api
      .get(`/messages/${selectedChatId}`, { params: { limit: 100 } })
      .then((res) => {
        if (!cancelled) setMessages(res.data.data || [])
      })
      .catch((error: any) => {
        if (cancelled) return
        toast({
          title: 'Erro ao carregar conversa',
          description: error.response?.data?.error || 'Tente novamente',
          status: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        if (!cancelled) setIsLoadingMessages(false)
      })

    socketService.joinChat(selectedChatId)
    const handleNewMessage = (data: any) => {
      if (Number(data.chatId) !== selectedChatId) return
      setMessages((prev) =>
        prev.some((m) => m.id === data.id) ? prev : [...prev, data],
      )
      setTypingName(null)
    }
    const handleTypingStart = (data: any) => {
      if (Number(data.chatId) !== selectedChatId) return
      setTypingName(data.userName || 'Digitando')
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      typingTimeoutRef.current = setTimeout(() => setTypingName(null), 3000)
    }
    const handleTypingStop = (data: any) => {
      if (Number(data.chatId) !== selectedChatId) return
      setTypingName(null)
    }
    socketService.on(SOCKET_EVENTS.MESSAGE.NEW, handleNewMessage)
    socketService.on(SOCKET_EVENTS.TYPING.STARTED, handleTypingStart)
    socketService.on(SOCKET_EVENTS.TYPING.STOPPED, handleTypingStop)

    return () => {
      cancelled = true
      socketService.off(SOCKET_EVENTS.MESSAGE.NEW, handleNewMessage)
      socketService.off(SOCKET_EVENTS.TYPING.STARTED, handleTypingStart)
      socketService.off(SOCKET_EVENTS.TYPING.STOPPED, handleTypingStop)
      socketService.leaveChat(selectedChatId)
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      setTypingName(null)
    }
  }, [selectedChatId, toast])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
  }, [messages, typingName])

  // ============ Intervenção ============

  const handleTakeOver = async (chat: AiChat) => {
    const confirmed = window.confirm(
      `Assumir o atendimento ${chat.protocol || `#${chat.id}`} de ${
        chat.userName
      }? O chamado passa para você e a IA para de responder.`,
    )
    if (!confirmed) return
    setActioningChatId(chat.id)
    try {
      await api.post(`/admin/ai-agents/chats/${chat.id}/take-over`)
      toast({
        title: 'Chamado assumido — ele agora está nos seus atendimentos',
        status: 'success',
        duration: 4000,
      })
      navigate(`/tickets/${chat.id}`)
    } catch (error: any) {
      toast({
        title: 'Erro ao assumir',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
      fetchChats(false)
    } finally {
      setActioningChatId(null)
    }
  }

  const handleRequeue = async (chat: AiChat) => {
    const confirmed = window.confirm(
      `Devolver o atendimento ${
        chat.protocol || `#${chat.id}`
      } para a fila humana? A IA para de responder e o cliente aguarda um atendente.`,
    )
    if (!confirmed) return
    setActioningChatId(chat.id)
    try {
      await api.post(`/admin/ai-agents/chats/${chat.id}/requeue`)
      toast({
        title: 'Chamado devolvido à fila humana',
        status: 'success',
        duration: 4000,
      })
      fetchChats(false)
    } catch (error: any) {
      toast({
        title: 'Erro ao devolver',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
      fetchChats(false)
    } finally {
      setActioningChatId(null)
    }
  }

  // ============ Render ============

  const staleCutoffMs = summary.staleHours * 3_600_000
  const isStale = (chat: AiChat) =>
    chat.state === 'ai_active' &&
    Date.now() - new Date(chat.updatedAt).getTime() > staleCutoffMs

  const filterChips: {
    key: string
    label: string
    count?: number
    color: string
  }[] = [
    { key: '', label: 'Todos', count: summary.total, color: 'gray' },
    {
      key: 'ai_active',
      label: 'Com a IA',
      count: summary.aiActive,
      color: 'green',
    },
    {
      key: 'ai_stale',
      label: `Parados +${summary.staleHours}h`,
      count: summary.aiStale,
      color: 'red',
    },
    {
      key: 'human_queue',
      label: 'Fila humana',
      count: summary.humanQueue,
      color: 'orange',
    },
    {
      key: 'human',
      label: 'Com atendente',
      count: summary.human,
      color: 'blue',
    },
    {
      key: 'completed',
      label: 'Concluídos',
      count: summary.completed,
      color: 'gray',
    },
  ]

  const showList = !isMobile || !selectedChat
  const showConversation = !isMobile || !!selectedChat

  const renderPreview = (chat: AiChat) => {
    if (!chat.lastMessage)
      return (
        <Text as="span" fontStyle="italic">
          Sem mensagens
        </Text>
      )
    const fromUser = chat.lastMessage.senderType === 'user'
    return (
      <>
        {!fromUser && chat.lastMessage.senderType === 'agent' && (
          <Box as="span" display="inline-flex" verticalAlign="-2px" mr={1}>
            <Robot size={14} />
          </Box>
        )}
        {chat.lastMessage.preview}
      </>
    )
  }

  const chatList = (
    <Flex
      direction="column"
      w={{ base: '100%', lg: '380px' }}
      flexShrink={0}
      borderRight={{ lg: '1px solid' }}
      borderColor={{ lg: borderColor }}
      minH={0}
      h="100%"
    >
      {/* Busca + agente */}
      <VStack
        spacing={2}
        p={3}
        align="stretch"
        borderBottom="1px solid"
        borderColor={borderColor}
      >
        <HStack spacing={2}>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none" color={textMuted}>
              <MagnifyingGlass size={16} />
            </InputLeftElement>
            <Input
              placeholder="Buscar cliente, protocolo ou nº"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              borderRadius="md"
            />
            {searchInput && (
              <InputRightElement>
                <IconButton
                  aria-label="Limpar busca"
                  icon={<X size={14} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => setSearchInput('')}
                />
              </InputRightElement>
            )}
          </InputGroup>
          <Tooltip label="Atualizar">
            <IconButton
              aria-label="Atualizar"
              icon={<ArrowsClockwise size={16} />}
              size="sm"
              variant="ghost"
              onClick={() => fetchChats(true)}
            />
          </Tooltip>
        </HStack>
        <Select
          size="sm"
          value={agentFilter}
          onChange={(e) => setAgentFilter(e.target.value)}
          borderRadius="md"
        >
          <option value="">Todos os agentes</option>
          {agents.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.name} — {agent.categoryName || 'sem categoria'}
            </option>
          ))}
        </Select>
        {/* Chips de situação (estilo filtros do WhatsApp) */}
        <Flex gap={1.5} flexWrap="wrap">
          {filterChips.map((chip) => {
            const active = stateFilter === chip.key
            return (
              <Button
                key={chip.key || 'all'}
                size="xs"
                borderRadius="full"
                variant={active ? 'solid' : 'ghost'}
                colorScheme={active ? chip.color : undefined}
                bg={active ? undefined : chipBg}
                fontWeight={active ? 'semibold' : 'normal'}
                onClick={() => setStateFilter(chip.key)}
              >
                {chip.label}
                {chip.count !== undefined && (
                  <Text as="span" ml={1.5} opacity={0.75}>
                    {chip.count}
                  </Text>
                )}
              </Button>
            )
          })}
        </Flex>
      </VStack>

      {/* Lista de conversas */}
      <Box flex={1} overflowY="auto" minH={0}>
        {isLoading ? (
          <Flex justify="center" py={10}>
            <Spinner color="yellow.500" />
          </Flex>
        ) : chats.length === 0 ? (
          <Text
            color={textMuted}
            fontSize="sm"
            textAlign="center"
            py={10}
            px={4}
          >
            {agentFilter || stateFilter || search
              ? 'Nenhum atendimento para este filtro.'
              : 'Nenhum atendimento passou pela IA ainda.'}
          </Text>
        ) : (
          <>
            {chats.map((chat) => {
              const selected = chat.id === selectedChatId
              const stale = isStale(chat)
              const lastAt = chat.lastMessage?.createdAt || chat.updatedAt
              return (
                <HStack
                  key={chat.id}
                  spacing={3}
                  px={3}
                  py={2.5}
                  cursor="pointer"
                  bg={selected ? selectedBg : undefined}
                  _hover={{ bg: selected ? selectedBg : hoverBg }}
                  borderBottom="1px solid"
                  borderColor={borderColor}
                  onClick={() => openChat(chat)}
                  align="start"
                >
                  <Avatar size="md" name={chat.userName} mt={0.5} />
                  <Box flex={1} minW={0}>
                    <HStack justify="space-between" spacing={2}>
                      <Text
                        color={textPrimary}
                        fontWeight="semibold"
                        fontSize="sm"
                        noOfLines={1}
                      >
                        {chat.userName}
                      </Text>
                      <Text
                        fontSize="xs"
                        color={
                          chat.unreadCountAgent > 0 ? 'green.400' : textMuted
                        }
                        flexShrink={0}
                      >
                        {formatListTime(lastAt)}
                      </Text>
                    </HStack>
                    <HStack justify="space-between" spacing={2} mt={0.5}>
                      <Text fontSize="sm" color={textMuted} noOfLines={1}>
                        {renderPreview(chat)}
                      </Text>
                      {chat.unreadCountAgent > 0 && (
                        <Badge
                          colorScheme="green"
                          variant="solid"
                          borderRadius="full"
                          fontSize="10px"
                          minW="20px"
                          textAlign="center"
                          flexShrink={0}
                        >
                          {chat.unreadCountAgent}
                        </Badge>
                      )}
                    </HStack>
                    <HStack spacing={1.5} mt={1} flexWrap="wrap">
                      <Badge
                        fontSize="9px"
                        colorScheme={CHAT_STATE_LABELS[chat.state].colorScheme}
                        variant="subtle"
                      >
                        {CHAT_STATE_LABELS[chat.state].label}
                      </Badge>
                      {stale && (
                        <Badge
                          fontSize="9px"
                          colorScheme="red"
                          variant="subtle"
                        >
                          <HStack spacing={0.5}>
                            <Clock size={10} />
                            <span>parado {formatIdle(chat.updatedAt)}</span>
                          </HStack>
                        </Badge>
                      )}
                      <Text fontSize="10px" color={textMuted} noOfLines={1}>
                        {chat.agentName} · {chat.protocol || `#${chat.id}`}
                      </Text>
                      {chat.categoryName && (
                        <Box
                          w="8px"
                          h="8px"
                          borderRadius="full"
                          bg={chat.categoryColor || 'gray.400'}
                          title={chat.categoryName}
                          flexShrink={0}
                        />
                      )}
                    </HStack>
                  </Box>
                </HStack>
              )
            })}
            {chats.length < total && (
              <Flex justify="center" py={3}>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => setLimit((l) => l + PAGE_SIZE)}
                >
                  Carregar mais ({total - chats.length})
                </Button>
              </Flex>
            )}
          </>
        )}
      </Box>
    </Flex>
  )

  const renderMessageBody = (msg: any, isOwn: boolean) => {
    switch (msg.messageType) {
      case 'image':
        return (
          <VStack align="stretch" spacing={1}>
            <Image
              src={msg.fileUrl || ''}
              alt={msg.caption || 'Imagem'}
              maxH="280px"
              maxW="280px"
              borderRadius="md"
              cursor="pointer"
              onClick={() =>
                msg.fileUrl && window.open(msg.fileUrl, '_blank', 'noopener')
              }
            />
            {msg.caption && (
              <Text fontSize="sm" whiteSpace="pre-wrap">
                {renderMessageWithLinks(msg.caption)}
              </Text>
            )}
          </VStack>
        )
      case 'video':
        return msg.fileUrl ? (
          <VideoThumbnail
            src={msg.fileUrl}
            maxWidth="260px"
            maxHeight="190px"
            isOwn={isOwn}
          />
        ) : (
          <Text fontSize="sm">🎥 Vídeo</Text>
        )
      case 'audio':
        return msg.fileUrl ? (
          <AudioPlayer src={msg.fileUrl} isOwn={isOwn} />
        ) : (
          <Text fontSize="sm">🎤 Áudio</Text>
        )
      case 'file':
        return (
          <HStack
            as={msg.fileUrl ? 'a' : undefined}
            {...(msg.fileUrl
              ? { href: msg.fileUrl, target: '_blank', rel: 'noreferrer' }
              : {})}
            spacing={2}
          >
            <FileText size={24} />
            <Text
              fontSize="sm"
              textDecoration={msg.fileUrl ? 'underline' : undefined}
              noOfLines={1}
            >
              {msg.fileName || 'Arquivo'}
            </Text>
          </HStack>
        )
      default:
        return (
          <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
            {renderMessageWithLinks(msg.message || '')}
          </Text>
        )
    }
  }

  const conversationItems = useMemo(() => {
    let lastDay = ''
    return messages.map((msg) => {
      const date = msg.createdAt ? new Date(msg.createdAt) : null
      const dayKey = date ? date.toDateString() : ''
      const separator =
        date && dayKey !== lastDay ? formatDaySeparator(date) : null
      lastDay = dayKey || lastDay
      return { msg, separator }
    })
  }, [messages])

  const conversation = selectedChat ? (
    <Flex direction="column" flex={1} minW={0} minH={0} h="100%">
      {/* Cabeçalho da conversa */}
      <HStack
        px={3}
        py={2}
        spacing={3}
        bg={headerBg}
        borderBottom="1px solid"
        borderColor={borderColor}
        flexShrink={0}
      >
        {isMobile && (
          <IconButton
            aria-label="Voltar"
            icon={<ArrowLeft size={18} />}
            size="sm"
            variant="ghost"
            onClick={() => setSelectedChat(null)}
          />
        )}
        <Avatar size="sm" name={selectedChat.userName} />
        <Box flex={1} minW={0}>
          <Text
            color={textPrimary}
            fontWeight="semibold"
            fontSize="sm"
            noOfLines={1}
          >
            {selectedChat.userName}
          </Text>
          <Text
            fontSize="xs"
            color={typingName ? 'green.400' : textMuted}
            noOfLines={1}
          >
            {typingName
              ? `${typingName} está digitando...`
              : `${selectedChat.protocol || `#${selectedChat.id}`} · ${
                  selectedChat.agentName
                }${
                  selectedChat.categoryName
                    ? ` · ${selectedChat.categoryName}`
                    : ''
                }`}
          </Text>
        </Box>
        <Badge
          colorScheme={CHAT_STATE_LABELS[selectedChat.state].colorScheme}
          flexShrink={0}
        >
          {CHAT_STATE_LABELS[selectedChat.state].label}
        </Badge>
        <HStack spacing={1} flexShrink={0}>
          {selectedChat.state === 'ai_active' && (
            <>
              <Tooltip label="Devolver à fila humana">
                <IconButton
                  aria-label="Devolver à fila"
                  icon={<ArrowUUpLeft size={18} />}
                  size="sm"
                  variant="ghost"
                  colorScheme="orange"
                  isLoading={actioningChatId === selectedChat.id}
                  onClick={() => handleRequeue(selectedChat)}
                />
              </Tooltip>
              <Tooltip label="Assumir o atendimento (a IA para de responder)">
                <IconButton
                  aria-label="Assumir"
                  icon={<UserSwitch size={18} />}
                  size="sm"
                  variant="ghost"
                  colorScheme="green"
                  isLoading={actioningChatId === selectedChat.id}
                  onClick={() => handleTakeOver(selectedChat)}
                />
              </Tooltip>
            </>
          )}
          {selectedChat.state === 'human' && (
            <Tooltip label="Abrir no atendimento">
              <IconButton
                aria-label="Abrir no atendimento"
                icon={<ArrowSquareOut size={18} />}
                size="sm"
                variant="ghost"
                onClick={() => navigate(`/tickets/${selectedChat.id}`)}
              />
            </Tooltip>
          )}
          {!isMobile && (
            <Tooltip label="Fechar conversa">
              <IconButton
                aria-label="Fechar conversa"
                icon={<X size={18} />}
                size="sm"
                variant="ghost"
                onClick={() => setSelectedChat(null)}
              />
            </Tooltip>
          )}
        </HStack>
      </HStack>

      {/* Mensagens */}
      <Box
        flex={1}
        overflowY="auto"
        minH={0}
        bg={wallpaperBg}
        px={{ base: 3, md: 8 }}
        py={4}
      >
        {isLoadingMessages ? (
          <Flex justify="center" py={10}>
            <Spinner color="yellow.500" />
          </Flex>
        ) : messages.length === 0 ? (
          <Text fontSize="sm" color={textMuted} textAlign="center" py={10}>
            Sem mensagens neste atendimento.
          </Text>
        ) : (
          <VStack align="stretch" spacing={1.5}>
            {conversationItems.map(({ msg, separator }) => {
              const time = msg.createdAt
                ? new Date(msg.createdAt).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : ''
              const separatorNode = separator && (
                <Flex justify="center" my={2}>
                  <Text
                    fontSize="xs"
                    bg={bubbleInBg}
                    color={bubbleMeta}
                    px={3}
                    py={1}
                    borderRadius="md"
                    boxShadow="sm"
                  >
                    {separator}
                  </Text>
                </Flex>
              )
              if (msg.senderType === 'system') {
                return (
                  <Fragment key={msg.id}>
                    {separatorNode}
                    <Flex justify="center">
                      <Text
                        fontSize="xs"
                        color={bubbleMeta}
                        bg={systemPillBg}
                        px={3}
                        py={1}
                        borderRadius="md"
                        textAlign="center"
                        maxW="85%"
                      >
                        {msg.message}
                      </Text>
                    </Flex>
                  </Fragment>
                )
              }
              const isOwn = msg.senderType === 'agent'
              return (
                <Fragment key={msg.id}>
                  {separatorNode}
                  <Flex justify={isOwn ? 'flex-end' : 'flex-start'}>
                    <Box
                      bg={isOwn ? bubbleOutBg : bubbleInBg}
                      color={bubbleText}
                      px={2.5}
                      pt={1.5}
                      pb={1}
                      borderRadius="lg"
                      borderTopRightRadius={isOwn ? 'sm' : 'lg'}
                      borderTopLeftRadius={isOwn ? 'lg' : 'sm'}
                      maxW={{ base: '88%', md: '70%' }}
                      boxShadow="sm"
                    >
                      {isOwn && msg.senderName && (
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          color="green.300"
                          mb={0.5}
                        >
                          {msg.senderName}
                        </Text>
                      )}
                      {renderMessageBody(msg, isOwn)}
                      <Text
                        fontSize="10px"
                        color={bubbleMeta}
                        textAlign="right"
                        mt={0.5}
                      >
                        {time}
                        {msg.editedAt ? ' · editada' : ''}
                      </Text>
                    </Box>
                  </Flex>
                </Fragment>
              )
            })}
            {typingName && (
              <Flex justify="flex-start">
                <HStack
                  bg={bubbleInBg}
                  px={3}
                  py={2}
                  borderRadius="lg"
                  spacing={2}
                  boxShadow="sm"
                >
                  <Spinner size="xs" color="green.400" />
                  <Text fontSize="sm" color={bubbleMeta}>
                    {typingName} está digitando...
                  </Text>
                </HStack>
              </Flex>
            )}
          </VStack>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Rodapé: monitoria é só leitura */}
      <HStack
        px={4}
        py={2.5}
        bg={headerBg}
        borderTop="1px solid"
        borderColor={borderColor}
        justify="space-between"
        flexShrink={0}
        spacing={3}
      >
        <Text fontSize="xs" color={textMuted}>
          {selectedChat.state === 'ai_active'
            ? 'Monitoria ao vivo — assuma o atendimento para responder ao cliente.'
            : selectedChat.state === 'completed'
            ? 'Atendimento concluído.'
            : 'Atendimento fora da IA.'}
        </Text>
        {selectedChat.state === 'ai_active' && (
          <Button
            size="sm"
            colorScheme="green"
            leftIcon={<UserSwitch size={16} />}
            isLoading={actioningChatId === selectedChat.id}
            onClick={() => handleTakeOver(selectedChat)}
            flexShrink={0}
          >
            Assumir
          </Button>
        )}
      </HStack>
    </Flex>
  ) : (
    <Flex
      flex={1}
      direction="column"
      align="center"
      justify="center"
      bg={wallpaperBg}
      color={textMuted}
      gap={3}
      p={6}
    >
      <ChatsCircle size={56} weight="thin" />
      <Text fontSize="sm" textAlign="center">
        Selecione uma conversa para acompanhar o atendimento em tempo real.
      </Text>
    </Flex>
  )

  return (
    <Flex
      bg={panelBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      h={{ base: 'calc(100vh - 170px)', lg: 'calc(100vh - 210px)' }}
      minH="520px"
    >
      {showList && chatList}
      {showConversation && conversation}
    </Flex>
  )
}
